import React, { useState, useEffect } from "react";
import { PostData, GetData, PutData, PostFileData } from "../../services/api";
import { useNavigate, useParams } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Button,
  FormGroup,
  Label,
  Input,
  Card,
  CardBody,
  Progress,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import Swal from "sweetalert2";
import Loader from "../../components/Common/Loader";

// ─── Questions definition ─────────────────────────────────────────────────────
// showIf(answers) → false means skip this step automatically

const PAGE_QUESTIONS = {
  venta_directa: [
    {
      key: "lf_v_propuesta",
      label: "Propuesta de valor principal",
      type: "textarea",
      placeholder:
        "Ej: Aprende a triplicar tus ventas en 90 días con un sistema probado, sin necesidad de experiencia previa",
      required: true,
    },
    {
      key: "lf_v_url_oferta",
      label: "URL del botón de compra / oferta",
      type: "url",
      placeholder: "https://pago.ejemplo.com/checkout",
      required: true,
    },
    {
      key: "lf_v_precio",
      label: "Precio del producto o servicio (opcional)",
      type: "text",
      placeholder: "Ej: $997 MXN, $97 USD, Desde $299",
    },
    {
      key: "lf_v_garantia",
      label: "Garantía o política de confianza (opcional)",
      type: "text",
      placeholder: "Ej: Garantía de 30 días, Sin compromiso, Cancela cuando quieras",
    },
    {
      key: "lf_v_cantidad_imagenes",
      label: "Número de imágenes en la página",
      type: "select",
      options: [
        { value: "2", label: "2 imágenes" },
        { value: "3", label: "3 imágenes" },
        { value: "4", label: "4 imágenes (recomendado)" },
        { value: "5", label: "5 imágenes" },
      ],
      default: "4",
    },
    {
      key: "lf_v_url_video",
      label: "URL de video (opcional)",
      type: "url",
      placeholder: "https://www.youtube.com/watch?v=... — déjalo vacío si no incluirás video",
    },
    {
      key: "lf_v_estilo_colores",
      label: "Estilo visual y colores",
      type: "estilo_colores",
      placeholder: "Ej: Fondo oscuro (#0a0a1a), primario púrpura (#6C63FF), botones verde lima (#a8ff3e). Estilo moderno tipo SaaS.",
    },
  ],
  webinar: [
    {
      key: "lf_w_titulo",
      label: "Título del Webinar",
      type: "text",
      placeholder: "Ej: Cómo Lanzar tu Negocio Digital en 30 Días",
      required: true,
    },
    {
      key: "lf_w_fecha",
      label: "Fecha del Webinar",
      type: "date",
      required: true,
    },
    {
      key: "lf_w_hora",
      label: "Hora del Webinar",
      type: "time",
      required: true,
    },
    {
      key: "lf_w_zona",
      label: "Zona horaria",
      type: "text",
      placeholder: "Ej: CDMX, Bogotá, Buenos Aires, Madrid",
      default: "CDMX",
    },
    {
      key: "lf_w_ponente",
      label: "Nombre del ponente / anfitrión",
      type: "text",
      placeholder: "Ej: Ana García",
    },
    {
      key: "lf_w_descripcion",
      label: "¿Qué aprenderán los participantes?",
      type: "textarea",
      placeholder: "Describe los beneficios y temas que se cubrirán en el webinar...",
    },
    {
      key: "lf_w_destino_leads",
      label: "Destino de los leads capturados",
      type: "text",
      placeholder: "Ej: ActiveCampaign, Mailchimp, https://tu-webhook.com/leads",
    },
    {
      key: "lf_w_url_acceso",
      label: "Link del evento (Zoom, Meet, YouTube Live)",
      type: "url",
      placeholder: "https://zoom.us/j/123456789",
    },
    {
      key: "lf_w_estilo_colores",
      label: "Estilo visual y colores",
      type: "estilo_colores",
      placeholder: "Ej: Fondo azul noche (#0d1b2a), primario dorado (#f5a623), estilo profesional tipo evento corporativo.",
    },
  ],
  gracias: [
    {
      key: "lf_g_titulo",
      label: "Título principal de la página",
      type: "text",
      placeholder: "Ej: ¡Registro confirmado!",
      required: true,
    },
    {
      key: "lf_g_mensaje",
      label: "Mensaje de confirmación",
      type: "textarea",
      placeholder:
        "Ej: Ya recibirás un correo con los detalles. Estamos emocionados de verte en el webinar.",
      required: true,
    },
    {
      key: "lf_g_siguiente_paso",
      label: "Descripción del siguiente paso",
      type: "textarea",
      placeholder:
        "Ej: Revisa tu bandeja de entrada, agrega el evento a tu calendario...",
    },
    {
      key: "lf_g_url_siguiente",
      label: "URL del botón / siguiente paso (opcional)",
      type: "url",
      placeholder: "https://ejemplo.com/siguiente",
    },
  ],
  linktree: [
    {
      key: "lf_l_nombre",
      label: "Tu nombre o nombre de la marca",
      type: "text",
      placeholder: "Ej: Ana García | Marketing Digital",
      required: true,
    },
    {
      key: "lf_l_bio",
      label: "Bio corta",
      type: "textarea",
      placeholder: "Ej: Ayudo a emprendedores a crecer en redes sociales.",
    },
    {
      key: "lf_l_links",
      label: "Links (Etiqueta | URL — uno por línea)",
      type: "textarea",
      placeholder:
        "Mi Instagram | https://instagram.com/tu_usuario\nMi WhatsApp | https://wa.me/521234567890\nMi Curso | https://tucurso.com",
      required: true,
    },
  ],
  vsl: [
    {
      key: "lf_s_url_video",
      label: "URL del video (YouTube, Vimeo, Loom)",
      type: "url",
      placeholder: "https://www.youtube.com/watch?v=...",
      required: true,
    },
    {
      key: "lf_s_hook",
      label: "Titular / Gancho principal",
      type: "text",
      placeholder: "Ej: El sistema que usé para pasar de 0 a 6 cifras sin invertir en publicidad",
      required: true,
    },
    {
      key: "lf_s_historia",
      label: "Historia de fondo (contexto)",
      type: "textarea",
      placeholder: "¿Cuál es tu historia? ¿De dónde partiste y a dónde llegaste?",
    },
    {
      key: "lf_s_mecanismo",
      label: "Mecanismo / Solución que ofreces",
      type: "textarea",
      placeholder: "¿Qué hace tu método diferente? ¿Cómo funciona tu solución?",
    },
    {
      key: "lf_s_prueba",
      label: "Prueba social (resultados, testimonios)",
      type: "textarea",
      placeholder:
        "Ej: Más de 500 alumnos han aplicado este método. María aumentó sus ventas 3× en 60 días.",
    },
    {
      key: "lf_s_cta",
      label: "Texto del botón CTA",
      type: "text",
      placeholder: "Quiero acceso ahora",
      default: "Quiero acceso ahora",
    },
    {
      key: "lf_s_url_cta",
      label: "URL del botón CTA",
      type: "url",
      placeholder: "https://pago.ejemplo.com/checkout",
      required: true,
    },
    {
      key: "lf_s_estilo_colores",
      label: "Estilo visual y colores",
      type: "estilo_colores",
      placeholder: "Ej: Oscuro con acento naranja (#FF6B35), tipografía bold, sensación de urgencia y exclusividad.",
    },
  ],
  captura: [
    {
      key: "lf_c_tipo_recurso",
      label: "¿Qué tipo de recurso gratuito ofreces?",
      type: "select",
      options: [
        { value: "ebook", label: "E-book o guía PDF" },
        { value: "checklist", label: "Checklist" },
        { value: "plantilla", label: "Plantilla o kit descargable" },
        { value: "mini_curso", label: "Mini-curso gratuito" },
        { value: "masterclass", label: "Masterclass o clase grabada" },
        { value: "descuento", label: "Descuento o cupón" },
        { value: "desafio", label: "Reto o desafío (challenge)" },
        { value: "otro", label: "Otro recurso gratuito" },
      ],
      required: true,
    },
    {
      key: "lf_c_titulo_recurso",
      label: "Título del recurso gratuito",
      type: "text",
      placeholder: "Ej: Guía de 30 recetas saludables en menos de 20 minutos",
      required: true,
    },
    {
      key: "lf_c_beneficios",
      label: "¿Qué aprenderán o recibirán? (3–5 puntos clave)",
      type: "textarea",
      placeholder: "Ej:\n• Cómo perder 5 kg sin dietas restrictivas\n• El error #1 que cometen quienes hacen ejercicio\n• La rutina de 10 minutos que transforma tu energía",
      required: true,
    },
    {
      key: "lf_c_url_video",
      label: "URL de video (opcional — YouTube, Vimeo, Loom)",
      type: "url",
      placeholder: "https://www.youtube.com/watch?v=... — déjalo vacío si no incluirás video",
    },
    // Campos del formulario: siempre nombre + apellido + email + teléfono (obligatorios)
    {
      key: "lf_c_destino_leads",
      label: "Destino de los leads capturados (opcional)",
      type: "text",
      placeholder: "Ej: ActiveCampaign, Mailchimp, https://tu-webhook.com/leads",
    },
    {
      key: "lf_c_url_gracias",
      label: "URL de la página de gracias (opcional)",
      type: "url",
      placeholder: "https://tu-sitio.com/gracias — el visitante será redirigido aquí al enviar el form",
    },
    {
      key: "lf_c_estilo_colores",
      label: "Estilo visual y colores",
      type: "estilo_colores",
      placeholder: "Ej: Fondo blanco, primario verde (#16a34a), estilo minimalista y fresco. O déjalo vacío para que la IA elija.",
    },
  ],
};

const TYPE_INFO = {
  venta_directa: {
    label: "Venta Directa",
    description:
      "Página long-form de venta con neuromarketing, múltiples imágenes y CTA hacia tu enlace de pago",
    icon: "mdi-cart",
  },
  webinar: {
    label: "Webinar",
    description: "Página de registro para webinar con formulario de captación y countdown",
    icon: "mdi-video",
  },
  gracias: {
    label: "Página de Gracias",
    description: "Página de confirmación post-acción con siguiente paso claro",
    icon: "mdi-check-circle",
  },
  linktree: {
    label: "Link in Bio",
    description: "Página estilo linktree con todos tus enlaces, mobile-first",
    icon: "mdi-link-variant",
  },
  vsl: {
    label: "VSL (Video Sales Letter)",
    description: "Página con video de ventas: gancho → historia → oferta → CTA",
    icon: "mdi-play-circle",
  },
  captura: {
    label: "Página de Captura",
    description: "Opt-in de lead magnet (ebook, checklist, masterclass…): formulario above the fold, recurso gratuito a cambio del email",
    icon: "mdi-email-plus",
  },
};

// ─── Component ─────────────────────────────────────────────────────────────────

const PageCreate = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);

  // Step 1
  const [pageName, setPageName] = useState("");
  const [pageType, setPageType] = useState("venta_directa");

  // Step 2 — identidad
  const [producto, setProducto] = useState("");
  const [marca, setMarca] = useState("");
  const [audiencia, setAudiencia] = useState("");
  const [emocion, setEmocion] = useState("");
  const [restricciones, setRestricciones] = useState("");

  // Step 3 — imágenes (todas opcionales)
  const [imagenPersonaUrl, setImagenPersonaUrl] = useState("");
  const [imagenLogoUrl, setImagenLogoUrl] = useState("");
  const [imagenProductoUrl, setImagenProductoUrl] = useState("");
  const [uploadingPersona, setUploadingPersona] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingProducto, setUploadingProducto] = useState(false);

  // Corporate identity brand colors (fetched from company)
  const [brandColors, setBrandColors] = useState(null);

  // Steps 3..N-1 — respuestas tipo-específicas
  const [answers, setAnswers] = useState({});

  // Step N — config
  const [availableLists, setAvailableLists] = useState([]);
  const [contactListId, setContactListId] = useState("");
  const [redirectUrl, setRedirectUrl] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [isPublished, setIsPublished] = useState(false);

  // Active questions (filtered by showIf)
  const allQuestions = PAGE_QUESTIONS[pageType] || [];
  const activeQuestions = allQuestions.filter(
    (q) => !q.showIf || q.showIf(answers)
  );
  const totalSteps = activeQuestions.length + 4;

  // ─── Data fetching ────────────────────────────────────────────────────────────

  useEffect(() => {
    const init = async () => {
      try {
        const lists = await GetData("/contact-lists");
        setAvailableLists(lists?.data || []);
      } catch {}

      // Fetch company brand colors
      try {
        const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
        if (authUser?.companyId) {
          const res = await GetData(`/companies/${authUser.companyId}`);
          const firstBrand = res?.data?.brand?.[0];
          if (firstBrand?.brandingColors?.color1) {
            setBrandColors(firstBrand.brandingColors);
          }
        }
      } catch {}
    };
    init();
    if (isEdit) fetchPage();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchPage = async () => {
    try {
      setFetching(true);
      const response = await GetData(`/pages/${id}`);
      const page = response.data;
      setPageName(page.name || "");
      setPageType(page.type || "venta_directa");
      setContactListId(page.contactListId || "");
      setRedirectUrl(page.redirectUrl || "");
      setIsActive(page.isActive !== undefined ? page.isActive : true);
      setIsPublished(page.isPublished !== undefined ? page.isPublished : false);

      const brief = page.answers || {};
      setProducto(brief.producto || "");
      setMarca(brief.marca || "");
      setAudiencia(brief.audiencia || "");
      setEmocion(brief.emocion || "");
      setRestricciones(brief.restricciones || "");
      setImagenPersonaUrl(brief.imagen_persona_url || "");
      setImagenLogoUrl(brief.logo_url || "");
      setImagenProductoUrl(brief.imagen_producto_url || "");
      setAnswers(brief.answers || {});
    } catch {
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo cargar la página" });
      navigate("/pages");
    } finally {
      setFetching(false);
    }
  };

  // When pageType changes, reset answers and go back to step 1
  const handleTypeChange = (newType) => {
    setPageType(newType);
    setAnswers({});
  };

  const handleAnswerChange = (key, value) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
  };

  // Apply brand colors to hex fields for the active type
  const applyBrandColors = () => {
    if (!brandColors) return;
    const isVsl     = pageType === "vsl";
    const isWebinar = pageType === "webinar";
    const isCaptura = pageType === "captura";
    const estiloKey = isVsl ? "lf_s_estilo_colores" : isWebinar ? "lf_w_estilo_colores" : isCaptura ? "lf_c_estilo_colores" : "lf_v_estilo_colores";
    const colorText = `Identidad corporativa: primario ${brandColors.color1}${brandColors.color2 ? `, secundario ${brandColors.color2}` : ""}. Úsalos como base de la paleta.`;
    setAnswers((prev) => ({ ...prev, [estiloKey]: colorText }));
  };

  // ─── Navigation ───────────────────────────────────────────────────────────────

  const canProceed = () => {
    if (currentStep === 1) return pageName.trim() !== "" && pageType !== "";
    if (currentStep === 2) return producto.trim() !== "";
    if (currentStep === 3) return true; // media step is always optional
    if (currentStep === totalSteps) return true;
    const qi = currentStep - 4;
    if (qi >= 0 && qi < activeQuestions.length) {
      const q = activeQuestions[qi];
      if (q.required) return !!answers[q.key];
    }
    return true;
  };

  const handleNext = () => {
    if (canProceed() && currentStep < totalSteps) {
      setCurrentStep((s) => s + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep((s) => s - 1);
  };

  // ─── Submit ───────────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const brief = {
        landing_type: pageType,
        producto,
        marca,
        audiencia,
        emocion,
        restricciones,
        imagen_persona: !!imagenPersonaUrl,
        imagen_producto: !!imagenProductoUrl,
        imagen_persona_url: imagenPersonaUrl || undefined,
        logo_url: imagenLogoUrl || undefined,
        imagen_producto_url: imagenProductoUrl || undefined,
        answers,
      };

      const payload = {
        name: pageName,
        brief,
        contactListId: contactListId || undefined,
        redirectUrl: redirectUrl || undefined,
        isActive,
        isPublished,
      };

      if (isEdit) {
        await PutData(`/pages/${id}`, payload);
        Swal.fire({
          icon: "success",
          title: "Guardado",
          text: "Los cambios se guardaron. La IA está regenerando el HTML en segundo plano.",
        });
      } else {
        await PostData("/pages", payload);
        Swal.fire({
          icon: "success",
          title: "¡Página creada!",
          text: "La IA está generando el contenido en segundo plano. Aparecerá lista en la lista en unos momentos.",
        });
      }
      navigate("/pages");
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "Error al guardar la página",
      });
    } finally {
      setLoading(false);
    }
  };

  // ─── Render helpers ───────────────────────────────────────────────────────────

  const renderQuestion = (question) => {
    const value =
      answers[question.key] !== undefined
        ? answers[question.key]
        : question.default || "";

    switch (question.type) {
      case "textarea":
        return (
          <Input
            type="textarea"
            rows="4"
            value={value}
            onChange={(e) => handleAnswerChange(question.key, e.target.value)}
            placeholder={question.placeholder}
          />
        );

      case "select":
        return (
          <Input
            type="select"
            value={value}
            onChange={(e) => handleAnswerChange(question.key, e.target.value)}
          >
            {!question.default && <option value="">Seleccionar...</option>}
            {question.options?.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Input>
        );

      case "estilo_colores":
        return (
          <div>
            <Input
              type="textarea"
              rows="3"
              value={value}
              onChange={(e) => handleAnswerChange(question.key, e.target.value)}
              placeholder={question.placeholder}
              style={{ fontFamily: "monospace", fontSize: 13 }}
            />
            <div
              className="mt-2 p-3 rounded"
              style={{ background: "#f0f4ff", border: "1px solid #d0d9f5", fontSize: 12, color: "#444", lineHeight: 1.6 }}
            >
              <strong style={{ display: "block", marginBottom: 4 }}>Cómo describir colores y estilo:</strong>
              <ul className="mb-0 ps-3" style={{ margin: 0 }}>
                <li><strong>Hex exacto</strong> — <span style={{ fontFamily: "monospace" }}>#FF5733, #1a1a2e</span></li>
                <li><strong>Nombre de color</strong> — azul marino, verde esmeralda, negro carbón</li>
                <li><strong>Referencia de marca</strong> — estilo Apple, paleta Notion, tipo Stripe</li>
                <li><strong>Emoción / tono</strong> — moderno y oscuro, vibrante y energético, limpio y minimalista</li>
                <li><strong>Descripción completa</strong> — "fondo blanco, primario azul (#2563eb), acento amarillo, sans-serif"</li>
              </ul>
              <span className="text-muted" style={{ display: "block", marginTop: 6 }}>Déjalo vacío para que la IA elija automáticamente según el producto y la audiencia.</span>
            </div>
          </div>
        );

      case "date":
        return (
          <Input
            type="date"
            value={value}
            onChange={(e) => handleAnswerChange(question.key, e.target.value)}
            min={new Date().toISOString().split("T")[0]}
          />
        );

      case "time":
        return (
          <Input
            type="time"
            value={value}
            onChange={(e) => handleAnswerChange(question.key, e.target.value)}
          />
        );

      case "number":
        return (
          <Input
            type="number"
            value={value}
            onChange={(e) => handleAnswerChange(question.key, e.target.value)}
            placeholder={question.placeholder}
            min={question.min}
            max={question.max}
          />
        );

      default:
        return (
          <Input
            type={question.type || "text"}
            value={value}
            onChange={(e) => handleAnswerChange(question.key, e.target.value)}
            placeholder={question.placeholder}
          />
        );
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────────

  if (fetching) {
    return (
      <div className="page-content">
        <Container fluid>
          <Loader size={150} />
        </Container>
      </div>
    );
  }

  const currentQuestion = activeQuestions[currentStep - 4];

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs
          title="IHG"
          breadcrumbItem={isEdit ? "Editar Página" : "Nueva Página"}
        />

        <Row>
          <Col lg={10} className="mx-auto">
            <Card>
              <CardBody>
                {/* Progress */}
                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-muted">Paso {currentStep} de {totalSteps}</span>
                    <span className="text-muted">
                      {Math.round((currentStep / totalSteps) * 100)}%
                    </span>
                  </div>
                  <Progress value={(currentStep / totalSteps) * 100} color="primary" />
                </div>

                {/* ── Step 1: Nombre y Tipo ── */}
                {currentStep === 1 && (
                  <div>
                    <h4 className="mb-4">Información Básica</h4>
                    <FormGroup>
                      <Label>Nombre de la Página <span className="text-danger">*</span></Label>
                      <Input
                        type="text"
                        value={pageName}
                        onChange={(e) => setPageName(e.target.value)}
                        placeholder="Ej: Webinar de Marketing Digital"
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Tipo de Página <span className="text-danger">*</span></Label>
                      <Input
                        type="select"
                        value={pageType}
                        onChange={(e) => handleTypeChange(e.target.value)}
                      >
                        {Object.entries(TYPE_INFO).map(([val, info]) => (
                          <option key={val} value={val}>{info.label}</option>
                        ))}
                      </Input>
                      {pageType && (
                        <small className="text-muted d-block mt-1">
                          {TYPE_INFO[pageType]?.description}
                        </small>
                      )}
                    </FormGroup>
                  </div>
                )}

                {/* ── Step 2: Identidad ── */}
                {currentStep === 2 && (
                  <div>
                    <h4 className="mb-1">Identidad del Producto</h4>
                    <p className="text-muted mb-4">
                      Esta información le da contexto a la IA para escribir el copy y elegir el diseño.
                    </p>

                    {/* Corporate identity colors preview */}
                    {brandColors?.color1 && (
                      <div
                        className="mb-4 p-3 border rounded"
                        style={{ background: "#f8f9fa", borderColor: "#dee2e6" }}
                      >
                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                          <div className="d-flex align-items-center gap-2">
                            <span className="text-muted small">
                              <i className="mdi mdi-palette-outline me-1" />
                              Identidad corporativa detectada:
                            </span>
                            <span
                              title={brandColors.color1}
                              style={{
                                display: "inline-block",
                                width: 20,
                                height: 20,
                                borderRadius: 4,
                                background: brandColors.color1,
                                border: "1px solid #ced4da",
                              }}
                            />
                            {brandColors.color2 && (
                              <span
                                title={brandColors.color2}
                                style={{
                                  display: "inline-block",
                                  width: 20,
                                  height: 20,
                                  borderRadius: 4,
                                  background: brandColors.color2,
                                  border: "1px solid #ced4da",
                                }}
                              />
                            )}
                            <small className="text-muted">
                              {brandColors.color1}
                              {brandColors.color2 ? ` · ${brandColors.color2}` : ""}
                            </small>
                          </div>
                          {(pageType === "venta_directa" || pageType === "vsl" || pageType === "webinar" || pageType === "captura") && (
                            <Button
                              size="sm"
                              color="outline-primary"
                              onClick={applyBrandColors}
                              style={{ fontSize: 12 }}
                            >
                              <i className="mdi mdi-check me-1" />
                              Usar estos colores
                            </Button>
                          )}
                        </div>
                      </div>
                    )}

                    <FormGroup>
                      <Label>Producto / Servicio / Tema <span className="text-danger">*</span></Label>
                      <Input
                        type="textarea"
                        rows="2"
                        value={producto}
                        onChange={(e) => setProducto(e.target.value)}
                        placeholder="Ej: Curso online de marketing digital para emprendedores"
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Nombre de la Marca</Label>
                      <Input
                        type="text"
                        value={marca}
                        onChange={(e) => setMarca(e.target.value)}
                        placeholder="Ej: EmprendePro"
                      />
                      <small className="text-muted">Aparecerá solo en el pie de página</small>
                    </FormGroup>

                    <FormGroup>
                      <Label>Audiencia Objetivo</Label>
                      <Input
                        type="textarea"
                        rows="2"
                        value={audiencia}
                        onChange={(e) => setAudiencia(e.target.value)}
                        placeholder="Ej: Emprendedores entre 25 y 45 años que quieren monetizar su conocimiento"
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Emoción / Tono Deseado</Label>
                      <Input
                        type="text"
                        value={emocion}
                        onChange={(e) => setEmocion(e.target.value)}
                        placeholder="Ej: Inspirador y aspiracional, pero cercano y sin tecnicismos"
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Restricciones o Advertencias (opcional)</Label>
                      <Input
                        type="text"
                        value={restricciones}
                        onChange={(e) => setRestricciones(e.target.value)}
                        placeholder="Ej: No mencionar precios, evitar lenguaje médico"
                      />
                    </FormGroup>
                  </div>
                )}

                {/* ── Step 3: Imágenes / medios (todas opcionales) ── */}
                {currentStep === 3 && (
                  <div>
                    <h4 className="mb-1">Imágenes de referencia</h4>
                    <p className="text-muted mb-4">
                      Todas son opcionales. Súbelas para que la IA las use como referencia visual al generar imágenes y en la página.
                    </p>

                    {/* Helper — upload slot */}
                    {[
                      {
                        id: "uploadPersona",
                        label: "Personaje / Presentador",
                        hint: "Foto del presentador, modelo o influencer. La IA generará imágenes coherentes con esta persona.",
                        icon: "mdi-account-circle-outline",
                        url: imagenPersonaUrl,
                        uploading: uploadingPersona,
                        onClear: () => setImagenPersonaUrl(""),
                        onUpload: async (file) => {
                          setUploadingPersona(true);
                          try {
                            const fd = new FormData();
                            fd.append("file", file);
                            const res = await PostFileData("/company/upload-image", fd);
                            setImagenPersonaUrl(res?.data?.url || "");
                          } catch {
                            Swal.fire({ icon: "error", title: "Error", text: "No se pudo subir la imagen" });
                          } finally {
                            setUploadingPersona(false);
                          }
                        },
                      },
                      {
                        id: "uploadLogo",
                        label: "Logo de marca",
                        hint: "Aparecerá en el pie de página de la landing. PNG con fondo transparente recomendado.",
                        icon: "mdi-watermark",
                        url: imagenLogoUrl,
                        uploading: uploadingLogo,
                        onClear: () => setImagenLogoUrl(""),
                        onUpload: async (file) => {
                          setUploadingLogo(true);
                          try {
                            const fd = new FormData();
                            fd.append("file", file);
                            const res = await PostFileData("/company/upload-image", fd);
                            setImagenLogoUrl(res?.data?.url || "");
                          } catch {
                            Swal.fire({ icon: "error", title: "Error", text: "No se pudo subir la imagen" });
                          } finally {
                            setUploadingLogo(false);
                          }
                        },
                      },
                      {
                        id: "uploadProducto",
                        label: "Producto / Empaque",
                        hint: "Foto del producto, empaque o mockup. La IA la usará en el showcase de la landing.",
                        icon: "mdi-package-variant-closed",
                        url: imagenProductoUrl,
                        uploading: uploadingProducto,
                        onClear: () => setImagenProductoUrl(""),
                        onUpload: async (file) => {
                          setUploadingProducto(true);
                          try {
                            const fd = new FormData();
                            fd.append("file", file);
                            const res = await PostFileData("/company/upload-image", fd);
                            setImagenProductoUrl(res?.data?.url || "");
                          } catch {
                            Swal.fire({ icon: "error", title: "Error", text: "No se pudo subir la imagen" });
                          } finally {
                            setUploadingProducto(false);
                          }
                        },
                      },
                    ].map(({ id, label, hint, icon, url, uploading, onClear, onUpload }) => (
                      <div key={id} className="mb-4 p-3 border rounded" style={{ background: "#f8f9fa" }}>
                        <div className="d-flex align-items-start gap-3">
                          <div style={{ fontSize: 28, lineHeight: 1, color: "#6c757d", flexShrink: 0 }}>
                            <i className={`mdi ${icon}`} />
                          </div>
                          <div style={{ flex: 1 }}>
                            <div className="fw-semibold mb-1" style={{ fontSize: 14 }}>{label}</div>
                            <small className="text-muted d-block mb-2">{hint}</small>
                            {url ? (
                              <div className="d-flex align-items-center gap-2">
                                <img
                                  src={url}
                                  alt={label}
                                  style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 6, border: "1px solid #ced4da", background: "#fff" }}
                                />
                                <Button size="sm" color="danger" outline onClick={onClear}>
                                  <i className="mdi mdi-close me-1" />Quitar
                                </Button>
                              </div>
                            ) : (
                              <div>
                                <label
                                  htmlFor={id}
                                  className="btn btn-sm btn-outline-secondary"
                                  style={{ cursor: uploading ? "not-allowed" : "pointer" }}
                                >
                                  {uploading ? (
                                    <><span className="spinner-border spinner-border-sm me-1" style={{ width: "0.7rem", height: "0.7rem" }} />Subiendo...</>
                                  ) : (
                                    <><i className="mdi mdi-upload me-1" />Subir imagen</>
                                  )}
                                </label>
                                <Input
                                  id={id}
                                  type="file"
                                  accept="image/*"
                                  style={{ display: "none" }}
                                  disabled={uploading}
                                  onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    await onUpload(file);
                                    e.target.value = "";
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}

                    <small className="text-muted">
                      <i className="mdi mdi-information-outline me-1" />
                      Si no subes ninguna imagen, la IA creará imágenes de stock coherentes con tu producto y audiencia.
                    </small>
                  </div>
                )}

                {/* ── Steps 4..N-1: Preguntas tipo-específicas ── */}
                {currentStep > 3 && currentStep < totalSteps && currentQuestion && (
                  <div>
                    <h4 className="mb-1">
                      {currentQuestion.label}
                      {currentQuestion.required && (
                        <span className="text-danger ms-1">*</span>
                      )}
                    </h4>
                    {currentQuestion.placeholder &&
                      currentQuestion.type !== "select" &&
                      currentQuestion.type !== "color" && (
                        <p className="text-muted small mb-3">
                          {currentQuestion.placeholder}
                        </p>
                      )}
                    <FormGroup>
                      {renderQuestion(currentQuestion)}
                    </FormGroup>
                  </div>
                )}

                {/* ── Último paso: Configuración Final ── */}
                {currentStep === totalSteps && (
                  <div>
                    <h4 className="mb-4">Configuración Final</h4>

                    <FormGroup>
                      <Label>Lista de Contactos</Label>
                      <Input
                        type="select"
                        value={contactListId}
                        onChange={(e) => setContactListId(e.target.value)}
                      >
                        <option value="">Seleccionar lista (opcional)</option>
                        {availableLists.map((list) => (
                          <option key={list.id} value={list.id}>
                            {list.name} ({list.count || 0} contactos)
                          </option>
                        ))}
                      </Input>
                      <small className="text-muted d-block mt-1">
                        Los leads capturados se agregarán a esta lista
                      </small>
                    </FormGroup>

                    <FormGroup>
                      <Label>URL de Redirección</Label>
                      <Input
                        type="url"
                        value={redirectUrl}
                        onChange={(e) => setRedirectUrl(e.target.value)}
                        placeholder="https://ejemplo.com/gracias"
                      />
                    </FormGroup>

                    <Row>
                      <Col md={6}>
                        <FormGroup check>
                          <Input
                            type="checkbox"
                            checked={isActive}
                            onChange={(e) => setIsActive(e.target.checked)}
                          />
                          <Label check>Página activa</Label>
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup check>
                          <Input
                            type="checkbox"
                            checked={isPublished}
                            onChange={(e) => setIsPublished(e.target.checked)}
                          />
                          <Label check>Publicada</Label>
                        </FormGroup>
                      </Col>
                    </Row>

                    {/* Resumen */}
                    <div className="mt-4 p-3 bg-light rounded">
                      <h6>Resumen:</h6>
                      <ul className="mb-0 small">
                        <li><strong>Nombre:</strong> {pageName}</li>
                        <li><strong>Tipo:</strong> {TYPE_INFO[pageType]?.label}</li>
                        {producto && <li><strong>Producto:</strong> {producto}</li>}
                        {marca && <li><strong>Marca:</strong> {marca}</li>}
                        {imagenPersonaUrl && <li><strong>Personaje:</strong> foto subida ✓</li>}
                        {imagenLogoUrl && <li><strong>Logo:</strong> subido ✓</li>}
                        {imagenProductoUrl && <li><strong>Producto:</strong> foto subida ✓</li>}
                        {brandColors?.color1 && !answers.lf_v_estilo_colores && !answers.lf_s_estilo_colores && !answers.lf_w_estilo_colores && !answers.lf_c_estilo_colores && (
                          <li>
                            <strong>Colores:</strong> Desde identidad corporativa (
                            <span style={{ fontFamily: "monospace" }}>{brandColors.color1}</span>)
                          </li>
                        )}
                        <li><strong>Respuestas:</strong> {Object.keys(answers).length} configuraciones</li>
                      </ul>
                    </div>

                    <div className="mt-3 p-3 border border-info rounded bg-info bg-opacity-10">
                      <small className="text-info-emphasis">
                        <i className="mdi mdi-robot-outline me-1" />
                        La IA generará el HTML en{" "}
                        <strong>segundo plano</strong> (30–90 seg). Puedes cerrar esta
                        ventana — la página aparecerá lista en la lista.
                      </small>
                    </div>
                  </div>
                )}

                {/* Navigation */}
                <div className="d-flex justify-content-between mt-4">
                  <Button
                    type="button"
                    color="light"
                    onClick={currentStep === 1 ? () => navigate("/pages") : handleBack}
                    disabled={loading}
                  >
                    <i className="mdi mdi-arrow-left me-1" />
                    {currentStep === 1 ? "Cancelar" : "Atrás"}
                  </Button>

                  {currentStep < totalSteps ? (
                    <Button
                      type="button"
                      color="primary"
                      onClick={handleNext}
                      disabled={!canProceed() || loading}
                    >
                      Siguiente
                      <i className="mdi mdi-arrow-right ms-1" />
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      color="success"
                      onClick={handleSubmit}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" />
                          {isEdit ? "Regenerando con IA..." : "Generando con IA..."}
                        </>
                      ) : isEdit ? (
                        "Guardar Página"
                      ) : (
                        "Crear Página"
                      )}
                    </Button>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default PageCreate;
